//App.js
import './App.css';
import Object from './components/ThreeD.js';
import TerminalInput from './components/TerminalInput.js';
import { ReactComponent as ScrollIndicator } from './assets/scroll-indicator.svg';
import { useState } from 'react';
import useTypewriter from './hooks/useTypewriter.js';
import TerminalEntry from './components/TerminalEntry.js';
import AnimatedCommand from './components/AnimatedCommand.js';
import FadeSection from './components/FadeSection.js';
import OrganizationsOutput from './components/OrganizationsOutput.js';


// Pages
import TicketShark from './pages/TicketShark.jsx';
import ViolationCrawler from './pages/ViolationCrawler.jsx';
import PosMapper from './pages/PosMapper.jsx'

function App() {
  const [history, setHistory] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showResponsibilities, setShowResponsibilities] = useState(false);

  const handleInput = (text) => {
    let output = null;
    switch (text.trim().toLowerCase()) {
      case '--help':
        output = (
          <ul>
            <li>--help: Lists available commands and their descriptions</li>
            <li>--projects: List my projects</li>
            <li>--orgs: List organizations I work with</li>
            <li>--contact: Displays contact methods</li>
            <li>--clear: Clears terminal history</li>
          </ul>
        );
        break;
      case '--projects':
        output = (
        <div className="projects-list">
          <button onClick={() => setSelectedProject('ticket-shark')} className='project-button'>
            Event Manager
          </button>
          <button onClick={() => setSelectedProject('violation-crawler')} className='project-button'>
            Violation Screener
          </button>
          <button onClick={() => setSelectedProject('pos-mapper')} className='project-button'>
            POS Mapper
          </button>
        </div>
        );
        break;
      case '--orgs':
        output = <OrganizationsOutput />;
        break;
      case '--contact':
        output = (
        <div className="links">
          <a href="mailto:2022akande@gmail.com" className="project-button">Email</a>
          <a href="https://www.linkedin.com/in/edward-akande-33136b1b4/" target="_blank" rel="noopener noreferrer" className="project-button">LinkedIn</a>
          <a href="https://github.com/2022akande" target="_blank" rel="noopener noreferrer" className="project-button">GitHub</a>
          <a href="https://stackoverflow.com/users/14342498/edward-akande" target="_blank" rel="noopener noreferrer" className="project-button">StackOverflow</a>
        </div>
        );
        break;
      case '--clear':
        setHistory([]);
        setSelectedProject(null);
        return;
      default:
        output = <p>Command not recognized: {text}</p>;
        break;
    }
    setHistory((prev) => [...prev, { command: text, output }]);
  };

  const renderProjectPage = () => {
    switch (selectedProject) {
      case 'ticket-shark':
        return <TicketShark />;
      case 'violation-crawler':
        return <ViolationCrawler />;
      case 'pos-mapper':
        return <PosMapper />;
      default:
        return null;
    }
  };

  
  const heroTitle = useTypewriter('Edward Akande - AI Engineer', 30);
  const heroText1 = useTypewriter(
    "As an AI FDE, I've dedicated myself to the creation, maintenance, and deployment of quality software experiences.",
    15,
    1200
  );
  const heroText3 = useTypewriter('Im always looking for interesting projects, contact me using the --contact command or scroll to explore.', 15, 6000);

  const commandsTitle = useTypewriter('TERMINAL COMMANDS', 30, 8000);
  const commandsList = [
    '--help: Lists available commands and their descriptions',
    '--projects: List a collection of projects',
    '--orgs: List organizations I work with',
    '--contact: Displays various contact methods',
    '--clear: Erases terminal history',
  ];

  return (
    <div className="terminal-window">
      <div className="terminal-header">
        <span className="dot red"></span>
        <span className="dot yellow"></span>
        <span className="dot green"></span>
        <span className="terminal-title">~/portfolio</span>
      </div>

      <div className="App">
        <div className="threeD-container">
          <Object />
        </div>

        <div className="content">
          {selectedProject ? (
            <div className="project-container">
              <button className="back-button" onClick={() => setSelectedProject(null)}>
                ← Back to Terminal
              </button>
              {renderProjectPage()}
            </div>
          ) : (
            <>
              <section className="hero-section">
                <h1>{heroTitle}</h1>
                <div className="colored-break"></div>
                <p>{heroText1}</p>
                <button
                  className="project-button"
                  onClick={() => setShowResponsibilities(r => !r)}
                >
                  Responsibilities
                </button>
                {showResponsibilities && (
                  <ul className="responsibilities-list">
                    <li>Technical scoping, system design, and production rollout of both general and AI-based applications.</li>
                    <li>Optimized Context Usage using RAG, Rank Over Stuffing, and other best practices.</li>
                    <li>Designed and implemented validation systems to ensure response accuracy.</li>
                    <li>Integrated agents into legacy systems and existing business logic.</li>
                  </ul>
                )}
                <p>{heroText3}</p>
              </section>

              <section className="commands-section">
                <h1>{commandsTitle}</h1>
                <ul>
                  {commandsList.map((cmd, i) => (
                    <li key={i}>
                      <AnimatedCommand text={cmd} delay={8500 + i * 1000} />
                    </li>
                  ))}
                </ul>

                <div className="terminal-history">
                  {history.map((entry, index) => (
                    <TerminalEntry key={index} entry={entry} />
                  ))}
                </div>

                <TerminalInput onEnter={handleInput} />
              </section>


              <div className="scroll-indicator-container">
                <ScrollIndicator className="scroll-indicator" />
              </div>
              

              {/* NEW SCROLL SECTIONS */}
              <FadeSection className="scroll-fade">
                <h1 className="scroll-section-title">Projects</h1>
                <div className="projects-list">
                  <button onClick={() => setSelectedProject('ticket-shark')} className='project-button'>
                    Event Manager
                  </button>
                  <button onClick={() => setSelectedProject('violation-crawler')} className='project-button'>
                    Violation Screener
                  </button>
                  <button onClick={() => setSelectedProject('pos-mapper')} className='project-button'>
                    POS Mapper
                  </button>
                </div>
              </FadeSection>

              <FadeSection className="scroll-fade">
                <h1 className="scroll-section-title">Organizations</h1>
                <OrganizationsOutput />
              </FadeSection>

              <FadeSection className="scroll-fade">
                <h1 className="scroll-section-title">Contact</h1>
                <div className="links">
                  <a href="mailto:2022akande@gmail.com" className="project-button">Email</a>
                  <a href="https://www.linkedin.com/in/edward-akande-33136b1b4/" target="_blank" rel="noopener noreferrer" className="project-button">LinkedIn</a>
                  <a href="https://github.com/2022akande" target="_blank" rel="noopener noreferrer" className="project-button">GitHub</a>
                  <a href="https://stackoverflow.com/users/14342498/edward-akande" target="_blank" rel="noopener noreferrer" className="project-button">StackOverflow</a>
                </div>
              </FadeSection>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;